import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { useTranslation } from '../../../hooks/useTranslation';

interface ChangeEmailDialogProps {
    open: boolean;
    isGoogleUser: boolean;
    isLoading: boolean;
    newEmail: string;
    currentPassword: string;
    onClose: () => void;
    onConfirm: () => void;
    onNewEmailChange: (value: string) => void;
    onCurrentPasswordChange: (value: string) => void;
}

const ChangeEmailDialog: React.FC<ChangeEmailDialogProps> = ({
    open,
    isGoogleUser,
    isLoading,
    newEmail,
    currentPassword,
    onClose,
    onConfirm,
    onNewEmailChange,
    onCurrentPasswordChange,
}) => {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    
    const [emailError, setEmailError] = useState<string>('');
    const [passwordError, setPasswordError] = useState<string>('');
    const [localEmail, setLocalEmail] = useState(newEmail);
    const [localPassword, setLocalPassword] = useState(currentPassword);
    
    // Display error messages in the notification system
    useEffect(() => {
        if (emailError || passwordError) {
            showNotification(emailError || passwordError, 'error');
        }
    }, [emailError, passwordError, showNotification]);
    
    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setEmailError('');
            setPasswordError('');
            setLocalEmail('');
            setLocalPassword('');
        } else {
            setLocalEmail(newEmail);
            setLocalPassword(currentPassword);
        }
    }, [open, newEmail, currentPassword]);
    
    // Validate email
    const validateEmail = (email: string): boolean => {
        if (!email) {
            setEmailError(t('auth.validation.required'));
            return false;
        }
        
        // Simple email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError(t('auth.validation.invalidEmailFormat'));
            return false;
        }
        
        setEmailError('');
        return true;
    };
    
    // Validate password
    const validatePassword = (password: string): boolean => {
        if (!password) {
            setPasswordError(t('auth.validation.required'));
            return false;
        }
        
        setPasswordError('');
        return true;
    };
    
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalEmail(value);
        onNewEmailChange(value);
        // Clear any existing error when typing
        setEmailError('');
    };
    
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalPassword(value);
        onCurrentPasswordChange(value);
        validatePassword(value);
    };
    
    const handleSubmit = () => {
        const isEmailValid = validateEmail(localEmail);
        const isPasswordValid = isGoogleUser || validatePassword(localPassword);
        
        if (isEmailValid && isPasswordValid) {
            onConfirm();
        }
    };
    
    const isFormValid = !!localEmail && (isGoogleUser || !!localPassword) && !emailError && !passwordError;
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
        >
            <DialogTitle>{t('settings.dialogs.changeEmail.title')}</DialogTitle>
            <DialogContent>
                <Stack sx={{ 
                    mt: 1, 
                    gap: 2 
                }}>
                    <TextField
                        type="email"
                        label={t('settings.dialogs.changeEmail.newEmail')}
                        fullWidth
                        value={localEmail}
                        onChange={handleEmailChange}
                        onBlur={() => validateEmail(localEmail)}
                        error={!!emailError}
                        helperText={emailError}
                        sx={{ mb: isGoogleUser ? 0 : 2 }}
                    />
                    
                    {!isGoogleUser && (
                        <TextField
                            type="password"
                            label={t('settings.dialogs.changeEmail.currentPassword')}
                            fullWidth
                            value={localPassword}
                            onChange={handlePasswordChange}
                            onBlur={() => validatePassword(localPassword)}
                            error={!!passwordError}
                            helperText={passwordError}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('settings.dialogs.changeEmail.buttons.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    disabled={!isFormValid || isLoading}
                >
                    {isLoading ? <CircularProgress size={24} /> : t('settings.dialogs.changeEmail.buttons.update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeEmailDialog;
